import { createSelector } from '@reduxjs/toolkit';

import { RootState } from '@/store';

import { CurrentStatus } from './model';

export const value = (state: RootState) => state.oneChatSettings.value;
export const request = (state: RootState) => state.oneChatSettings.request;
export const currentStatus = (state: RootState) =>
  state.oneChatSettings.currentStatus;

export const isLoaded = createSelector(value, (v) => Boolean(v));
export const isLoading = createSelector(request, (r) => r === 'loading');
export const isError = createSelector(request, (r) => r === 'error');
export const neverLoaded = createSelector(
  value,
  request,
  (v, r) => v === null && r === 'idle',
);

export const isAi = createSelector(
  currentStatus,
  (s) => s === CurrentStatus.AI_ASSISTANT_ONLINE,
);

export const isManual = createSelector(
  currentStatus,
  (s) => s === CurrentStatus.MANUAL_ONLINE,
);

export const isOffline = createSelector(
  currentStatus,
  (s) => s === CurrentStatus.OFFLINE,
);

export const widgetSettings = createSelector(value, (v) => v);

export const commonBehavior = createSelector(
  widgetSettings,
  (settings) => settings?.commonBehavior,
);

export const offlineBehavior = createSelector(
  widgetSettings,
  (settings) => settings?.offlineBehavior,
);

export const aiBehavior = createSelector(
  widgetSettings,
  (settings) => settings?.aiBehavior,
);

export const manualBehaviour = createSelector(
  widgetSettings,
  (settings) => settings?.wixUserBehavior,
);

export const manualContactFormBefore = createSelector(
  manualBehaviour,
  (behavior) => Boolean(behavior?.contactFormBeforeChat),
);

export const aiContactFormBefore = createSelector(aiBehavior, (behavior) =>
  Boolean(behavior?.showContactFormBeforeChat),
);

export const manualContactFormAfterMessage = createSelector(
  manualBehaviour,
  (behavior) => Boolean(behavior?.contactFormOnFirstMessage),
);

export const manualAllowChat = createSelector(manualBehaviour, (behavior) =>
  Boolean(behavior?.allowChat),
);

export const autoOpen = createSelector(
  commonBehavior,
  (behavior) => behavior?.autoOpen,
);
export const appearance = createSelector(
  commonBehavior,
  (behavior) => behavior?.appearance,
);

export const aiIntroMessageSettings = createSelector(
  aiBehavior,
  (behavior) => behavior?.introduction,
);

export const manualIntroMessageSettings = createSelector(
  manualBehaviour,
  (behavior) => behavior?.introduction,
);

export const aiLegalMessageSettings = createSelector(
  aiBehavior,
  (behavior) => behavior?.legalDisclaimer,
);

export const engageMessageSettings = createSelector(
  aiBehavior,
  (behavior) => behavior?.engage,
);

export const offlineIntroMessageSettings = createSelector(
  offlineBehavior,
  (behavior) => behavior?.introduction,
);

export const suggestionsSettings = createSelector(
  aiBehavior,
  (behavior) => behavior?.suggestions,
);

export const shouldShowSuggestionChips = createSelector(
  suggestionsSettings,
  (s) => s?.displayWhenMinimized ?? true,
);

export const contactFormFields = createSelector(
  commonBehavior,
  (behavior) => behavior?.contactForm?.fields,
);

export const isReportingEnabled = createSelector(aiBehavior, (behavior) =>
  Boolean(behavior?.includeNavigationSummary),
);

export const aiAvatar = createSelector(
  aiBehavior,
  (behavior) => behavior?.avatar,
);

export const manualAvatar = createSelector(
  manualBehaviour,
  (behavior) => behavior?.avatar,
);

export const allowFileUploadInOfflineMode = createSelector(
  currentStatus,
  offlineBehavior,
  (status, behavior) =>
    status === CurrentStatus.OFFLINE && Boolean(behavior?.allowFileUpload),
);
